import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, TextField, Typography, Alert,
} from '@mui/material';
import { adjustCustomerCredits } from '../api';

const parsePositiveInt = (value) => {
    if (typeof value !== 'string' || !/^\d+$/.test(value)) return null;
    const n = Number(value);
    if (!Number.isInteger(n) || n <= 0) return null;
    return n;
};

const CreditAdjustDialog = ({ open, operation, customerId, remaining, onClose, onSuccess }) => {
    const isAssign = operation === 'assign';
    const available = Math.max(0, remaining ?? 0);
    const [credits, setCredits] = useState('');
    const [note, setNote] = useState('');
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        setCredits('');
        setNote('');
        setSaving(false);
        setError(!isAssign && available <= 0 ? 'No credits remaining to deduct.' : '');
    }, [open, isAssign, available]);

    const reset = () => {
        setCredits('');
        setNote('');
        setError('');
        setSaving(false);
    };

    const handleClose = () => {
        if (saving) return;
        reset();
        onClose();
    };

    const onCreditsChange = (e) => {
        const next = e.target.value.replace(/\D/g, '');
        setCredits(next);

        const amount = parsePositiveInt(next);
        if (!next) {
            setError('');
            return;
        }
        if (!amount) {
            setError('Enter a positive integer.');
            return;
        }
        if (!isAssign && amount > available) {
            setError(`Cannot deduct more than ${available} remaining credit${available === 1 ? '' : 's'}.`);
            return;
        }
        setError('');
    };

    const amount = parsePositiveInt(credits);
    const canSubmit = Boolean(amount)
        && (isAssign || amount <= available)
        && (isAssign || available > 0)
        && !saving;

    const handleSubmit = async () => {
        if (!amount) {
            setError('Enter a positive integer.');
            return;
        }
        if (!isAssign && amount > available) {
            setError(`Cannot deduct more than ${available} remaining credit${available === 1 ? '' : 's'}.`);
            return;
        }

        setSaving(true);
        setError('');
        try {
            const res = await adjustCustomerCredits(customerId, {
                operation,
                credits: amount,
                note: note.trim() || undefined,
            });
            onSuccess(res.data.wallet, res.data.message);
            reset();
            onClose();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to update credits');
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
            <DialogTitle>{isAssign ? 'Assign credits' : 'Deduct credits'}</DialogTitle>
            <DialogContent>
                <Typography sx={{ color: '#6b7280', fontSize: '0.875rem', mb: 2 }}>
                    {available} credits currently remaining. Logged as system grant.
                </Typography>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                <TextField
                    autoFocus
                    fullWidth
                    type="text"
                    inputMode="numeric"
                    label="Credits"
                    value={credits}
                    onChange={onCreditsChange}
                    disabled={!isAssign && available <= 0}
                    inputProps={{
                        inputMode: 'numeric',
                        pattern: '[0-9]*',
                        maxLength: 10,
                    }}
                    helperText={isAssign ? 'Whole number greater than 0' : `Max ${available}`}
                    margin="dense"
                />
                <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    label="Note"
                    placeholder="e.g. Offline data request fulfilled"
                    value={note}
                    onChange={(e) => setNote(e.target.value.slice(0, 255))}
                    helperText={`${note.length}/255`}
                    margin="dense"
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={saving} sx={{ textTransform: 'none' }}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={!canSubmit}
                    color={isAssign ? 'primary' : 'error'}
                    sx={{ textTransform: 'none' }}
                >
                    {saving ? 'Saving…' : isAssign ? 'Assign' : 'Deduct'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CreditAdjustDialog;
